// ── WHICH SALES VIDEO WAS LIVE WHEN — FOR THE VIDEO FIGURES ONLY ────────────
//
// Since 2026-09-30 there is no sales video switch: every sales page figure
// follows the tab's own date range. THE ONE EXCEPTION is the sales video's
// watch figures. v1 (usqff3xnr0) and v2 (50s0ufyhoe) are different lengths,
// so their watching must never be blended: each video is counted only over its
// own live period WITHIN the range, against its own length, from its own
// records — exactly the figures the old switch produced for that video.
//
//   range touches one video's period  → one block, that video
//   range spans the change            → one block per video, labelled
//
// The periods come from GET /api/admin/sales-videos (backend
// config/salesVideos.js + ry_settings.sales_video_periods), so a v3, or v1
// going live again, needs no change here.
//
// salesWindow / salesExtra are the old switch's own functions, moved here
// unchanged, so a video's window and params are byte-for-byte what the switch
// sent for it.

import { useEffect, useMemo, useState } from 'react';
import { getSalesVideos } from '../../utils/api';

const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/;
// The same reading of ?from/?to the backend applies (config/launch.js).
const toInstant = (raw, endOfDay) => {
  if (!raw) return null;
  if (BARE_DATE.test(raw)) return Date.parse(`${raw}${endOfDay ? 'T23:59:59.999Z' : 'T00:00:00.000Z'}`);
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
};
const iso = (ms) => new Date(ms).toISOString();

const fmtDay = (ms) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtMoment = (ms) => new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtShortMoment = (ms) => new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * The overlap of the tab's range with a video's live period.
 * @returns {{from, to, gaps, empty, fromMs, toMs, text}} — from/to are the
 *   exact values to send (the tab's own strings when not narrowed).
 */
export function salesWindow(video, launch, from, to) {
  if (!video) return { from, to, gaps: '', empty: false, text: '' };
  const pf = toInstant(from, false) ?? Date.parse(launch);
  const pt = toInstant(to, true);
  const vf = Date.parse(video.spanFrom);
  const vt = video.spanTo ? Date.parse(video.spanTo) - 1 : null;   // live period ends exclusive
  const ef = Math.max(pf, vf);
  const et = pt == null ? vt : vt == null ? pt : Math.min(pt, vt);
  const empty = et != null && et < ef;
  const gapsIn = (video.gaps || []).filter(([a, b]) => Date.parse(b) > ef && (et == null || Date.parse(a) <= et));
  const text = empty
    ? `${video.label} was not on the page in this date range`
    : `${fmtMoment(ef)} → ${et == null ? 'now' : fmtMoment(et)}` +
      (gapsIn.length ? ` (leaving out ${gapsIn.map(([a, b]) => `${fmtDay(Date.parse(a))}–${fmtDay(Date.parse(b))}`).join(', ')}, when another video was live)` : '');
  return {
    from: ef === pf ? from : iso(ef),
    to: et === pt ? to : (et == null ? undefined : iso(et)),
    gaps: gapsIn.map(([a, b]) => `${a}~${b}`).join(','),
    empty, fromMs: ef, toMs: et, text,
  };
}

/** Extra query params for a video's window: the video (video endpoints), and any gaps. */
export function salesExtra(videoId, win, { withVideo = false } = {}) {
  const out = {};
  if (withVideo && videoId) out.salesVideo = videoId;
  if (win?.gaps) out.gaps = win.gaps;
  return out;
}

/**
 * "Video v1 · until 30 Sept, 00:50" / "Video v2 · from 30 Sept, 00:50" — the
 * part of the range this video's block covers, in the viewer's own time.
 */
export function videoWindowLabel(video, win, from, to) {
  if (!video || !win) return '';
  // salesWindow hands back the tab's own from/to when the video does not narrow
  // them, so "narrowed" is simply "not the tab's own value".
  const cutStart = win.from !== from;
  const cutEnd = win.to !== to;
  if (cutStart && !cutEnd) return `Video ${video.label} · from ${fmtShortMoment(win.fromMs)}`;
  if (!cutStart && cutEnd) return `Video ${video.label} · until ${fmtShortMoment(win.toMs)}`;
  if (cutStart && cutEnd) return `Video ${video.label} · ${fmtShortMoment(win.fromMs)} – ${fmtShortMoment(win.toMs)}`;
  return `Video ${video.label}`;
}

let infoCache = null;

/**
 * Every sales video whose live period overlaps [from, to], oldest first, each
 * with its window. `split` = more than one video in the range.
 */
export function useSalesVideoWindows(from, to) {
  const [info, setInfo] = useState(infoCache);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (infoCache) return;
    getSalesVideos()
      .then(r => { infoCache = r.data; setInfo(r.data); })
      .catch(() => setFailed(true));
  }, []);
  const windows = useMemo(() => {
    if (!info) return [];
    return (info.videos || [])
      .map(v => ({ video: v, win: salesWindow({ ...v, launch: info.launch }, info.launch, from, to) }))
      .filter(x => !x.win.empty)
      .map(x => ({ ...x, label: videoWindowLabel(x.video, x.win, from, to) }));
  }, [info, from, to]);
  return {
    ready: !!info, failed, windows, split: windows.length > 1,
    info, videos: info?.videos || [],
    // Changes whenever the video figures must refetch.
    key: info ? windows.map(w => `${w.video.id}|${w.win.from ?? ''}|${w.win.to ?? ''}|${w.win.gaps}`).join(';') + `|${from ?? ''}|${to ?? ''}` : null,
  };
}
